/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { Zap, Cloud, Thermometer, Droplets, Heart } from "lucide-react";

/**
 * WaterVsFlameTool, an interactive, calm comparison explorer.
 * Families select an aspect and watch water cremation and flame cremation
 * compared side by side, with gentle animated impact meters where relevant.
 */

const WATER = "#9CAF88"; // sage-500, the gentler option
const FLAME = "#C08A6A"; // soft, desaturated ember/clay, flame option

const aspects = [
  {
    key: "energy",
    label: "Energy Use",
    icon: Zap,
    summary: "How much energy each process requires.",
    metric: { unit: "Relative energy used" },
    water: { value: 12, headline: "Around 90% less energy", detail: "A gentle, low-energy process driven by warm water and chemistry, no combustion required." },
    flame: { value: 100, headline: "High, sustained energy", detail: "Significant natural gas is needed to hold combustion temperatures for the duration of the process." },
  },
  {
    key: "emissions",
    label: "Carbon Emissions",
    icon: Cloud,
    summary: "What is released into the atmosphere.",
    metric: { unit: "Relative direct emissions" },
    water: { value: 6, headline: "No direct airborne emissions", detail: "No flame means no smoke, no particulate matter, and no direct greenhouse gas emissions." },
    flame: { value: 100, headline: "Direct CO₂ for every cremation", detail: "Combustion produces carbon dioxide and trace airborne emissions each time." },
  },
  {
    key: "process",
    label: "Heat & Process",
    icon: Thermometer,
    summary: "The conditions at the heart of each method.",
    metric: { unit: "Relative process temperature" },
    water: { value: 10, headline: "≈ 95°C, warm water", detail: "Warm water, gentle circulation, and a small amount of alkali accelerate a natural process." },
    flame: { value: 100, headline: "≈ 900°C, open flame", detail: "Intense, sustained heat and oxidation reduce the body to ash over several hours." },
  },
  {
    key: "remains",
    label: "The Remains",
    icon: Droplets,
    summary: "What is returned to your family.",
    water: { headline: "Soft, fine, pale powder", detail: "Light in colour and fine in texture, many families describe it as similar to soft sand." },
    flame: { headline: "Coarser grey ash", detail: "Traditional ash, typically grey in colour and coarser in texture." },
  },
  {
    key: "experience",
    label: "The Experience",
    icon: Heart,
    summary: "How the goodbye tends to feel.",
    water: { headline: "Quiet, slow, considered", detail: "Often described as gentle and natural. Warmth, water, and time." },
    flame: { headline: "Efficient, traditional", detail: "A long-established and familiar option for many families." },
  },
];

function ImpactBar({ label, value, color, animate }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-sm font-medium text-forest-800">{label}</span>
        <span className="text-xs text-forest-500 tabular-nums">{value}%</span>
      </div>
      <div className="h-3 rounded-full bg-sand-200 overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-[1100ms] ease-out"
          style={{ width: animate ? `${value}%` : "0%", backgroundColor: color }}
        />
      </div>
    </div>
  );
}

export default function WaterVsFlameTool() {
  const [activeKey, setActiveKey] = useState(aspects[0].key);
  const [animate, setAnimate] = useState(false);

  const active = aspects.find((a) => a.key === activeKey);

  useEffect(() => {
    setAnimate(false);
    const t = setTimeout(() => setAnimate(true), 60);
    return () => clearTimeout(t);
  }, [activeKey]);

  const ActiveIcon = active.icon;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12" data-testid="water-vs-flame-tool">
      {/* Aspect selector */}
      <div className="lg:col-span-4">
        <div className="overline mb-5">Choose An Aspect</div>
        <div
          className="flex lg:flex-col gap-3 overflow-x-auto no-scrollbar pb-1"
          role="tablist"
          aria-label="Comparison aspects"
        >
          {aspects.map((a) => {
            const Icon = a.icon;
            const isActive = a.key === activeKey;
            return (
              <button
                key={a.key}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveKey(a.key)}
                data-testid={`compare-aspect-${a.key}`}
                className={`group flex items-center gap-4 shrink-0 lg:w-full text-left rounded-2xl border px-5 py-4 transition-all duration-300 ${
                  isActive
                    ? "bg-forest-900 border-forest-900 text-sand-50 shadow-[0_10px_30px_rgba(42,59,50,0.18)]"
                    : "bg-white border-sand-300/70 text-forest-800 hover:border-forest-900/30 hover:-translate-y-0.5"
                }`}
              >
                <span
                  className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                    isActive ? "bg-sand-50/15 text-sand-50" : "bg-sage-50 text-sage-700"
                  }`}
                >
                  <Icon size={17} strokeWidth={1.6} />
                </span>
                <span className="font-serif text-lg sm:text-xl leading-tight tracking-tight">
                  {a.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Comparison panel */}
      <div className="lg:col-span-8">
        <div
          key={active.key}
          className="bg-white border border-sand-300/70 rounded-3xl p-7 sm:p-10 animate-fade-in-up"
          data-testid="compare-panel"
        >
          <div className="flex items-center gap-3 mb-2">
            <ActiveIcon size={18} strokeWidth={1.6} className="text-sage-600" />
            <div className="overline">{active.label}</div>
          </div>
          <p className="text-forest-700 leading-relaxed mb-8 max-w-xl">{active.summary}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Water */}
            <div
              className="rounded-2xl border border-sage-100 bg-sage-50/60 p-6"
              data-testid="compare-water-column"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: WATER }} />
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-sage-700">
                  Water Cremation
                </span>
              </div>
              <p className="font-serif text-2xl text-forest-900 leading-tight tracking-tight mb-2">
                {active.water.headline}
              </p>
              <p className="text-forest-700 text-[15px] leading-relaxed">{active.water.detail}</p>
            </div>

            {/* Flame */}
            <div
              className="rounded-2xl border border-sand-300/70 bg-sand-50 p-6"
              data-testid="compare-flame-column"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: FLAME }} />
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-forest-500">
                  Flame Cremation
                </span>
              </div>
              <p className="font-serif text-2xl text-forest-900 leading-tight tracking-tight mb-2">
                {active.flame.headline}
              </p>
              <p className="text-forest-700 text-[15px] leading-relaxed">{active.flame.detail}</p>
            </div>
          </div>

          {/* Impact meters (quantitative aspects only) */}
          {active.metric && (
            <div className="mt-8 pt-8 border-t border-sand-300/70" data-testid="compare-meters">
              <div className="overline mb-6">{active.metric.unit}</div>
              <div className="space-y-6">
                <ImpactBar label="Water Cremation" value={active.water.value} color={WATER} animate={animate} />
                <ImpactBar label="Flame Cremation" value={active.flame.value} color={FLAME} animate={animate} />
              </div>
              <p className="text-xs text-forest-500 mt-5 leading-relaxed">
                Indicative comparison for illustration. Lower values reflect a lighter footprint.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
